import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { actressApi } from '../utils/apiConfig';
import ActressStats from './ActressStats';
import ActressWorksList from './ActressWorksList';
import ActressGallery from './ActressGallery';
import ActressSocialLinks from './ActressSocialLinks';
import ActressProfileDisplay from './ActressProfileDisplay';

const ActressDetailEnhanced = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchEnhancedProfile = useCallback(async () => {
    try {
      setLoading(true);
      // Use regular endpoint with includeProfile parameter
      const response = await actressApi.getById(id);
      console.log('Actress profile data:', response.data);
      
      // Map the data to match expected structure
      const profileData = {
        ...response.data,
        dmm: {
          birthdate: response.data.birthdate,
          height: response.data.height,
          bust: response.data.bust,
          waist: response.data.waist,  
          hip: response.data.hip,
          cup: response.data.cup,
          bloodType: response.data.bloodType,
          debutDate: response.data.debutDate,
          profileImage: response.data.profileImage
        },
        additionalInfo: {
          birthplace: response.data.birthplace,
          hobby: response.data.hobby
        },
        social: {},
        images: { all: [], profile: [] },
        stats: null,
        aliases: []
      };
      
      setProfile(profileData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch actress profile:', err);
      setError('女優情報の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEnhancedProfile();
  }, [fetchEnhancedProfile]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP');
  };

  const formatPhysicalStats = () => {
    // DMM情報を優先的に使用
    const dmmInfo = profile.dmm;
    const additionalInfo = profile.additionalInfo;
    
    const stats = [];
    
    // 身長（DMM優先）
    const height = dmmInfo?.height || additionalInfo?.height;
    if (height) stats.push(`${height}cm`);
    
    // スリーサイズ（DMM優先）
    const bust = dmmInfo?.bust || additionalInfo?.bust;
    const waist = dmmInfo?.waist || additionalInfo?.waist;
    const hip = dmmInfo?.hip || additionalInfo?.hip;
    
    if (bust || waist || hip) {
      const measurements = [
        bust ? `B${bust}` : '',
        waist ? `W${waist}` : '',
        hip ? `H${hip}` : ''
      ].filter(Boolean).join('-');
      if (measurements) stats.push(measurements);
    }
    
    // 血液型（DMM優先）
    const bloodType = dmmInfo?.bloodType || additionalInfo?.bloodType;
    if (bloodType) stats.push(`${bloodType}型`);
    
    return stats.length > 0 ? stats.join(' / ') : null;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">読み込み中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-600">{error}</p>
          <button
            onClick={fetchEnhancedProfile}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            再試行
          </button>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900">女優が見つかりません</h1>
          <Link to="/actresses" className="mt-4 text-blue-600 hover:underline">
            女優一覧に戻る
          </Link>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: '概要', count: null },
    { id: 'works', label: '作品', count: profile.videos?.total || 0 },
    { id: 'gallery', label: 'ギャラリー', count: profile.images?.all?.length || 0 },
    { id: 'profile', label: 'プロフィール', count: null }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      {/* Breadcrumb */}
      <nav className="mb-6">
        <Link to="/actresses" className="text-blue-600 hover:underline">
          女優一覧
        </Link>
        <span className="mx-2 text-gray-500">›</span>
        <span className="text-gray-900">{profile.name}</span>
      </nav>

      {/* Header Section */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <div className="flex flex-col md:flex-row">
          {/* Profile Image */}
          <div className="md:w-1/4 mb-6 md:mb-0 md:pr-6">
            {profile.profileImage ? (
              <img
                src={profile.profileImage}
                alt={profile.name}
                className="w-full max-w-xs rounded-lg object-cover mx-auto"
                onError={(e) => {
                  console.error('Image load error:', e.target.src);
                  e.target.onerror = null;
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200" viewBox="0 0 200 200"%3E%3Crect width="200" height="200" fill="%23e5e7eb"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%236b7280" font-family="sans-serif" font-size="14"%3ENo Image%3C/text%3E%3C/svg%3E';
                }}
              />
            ) : (
              <div className="w-full max-w-xs h-64 bg-gray-200 rounded-lg flex items-center justify-center mx-auto">
                <span className="text-gray-500">No Image</span>
              </div>
            )}
          </div>

          {/* Basic Info */}
          <div className="md:w-3/4">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h1 className="text-3xl font-bold mb-2">{profile.name}</h1>
                {profile.name_kana && (
                  <p className="text-xl text-gray-600 mb-2">{profile.name_kana}</p>
                )}
                
                {/* Aliases */}
                {profile.aliases && profile.aliases.length > 0 && (
                  <div className="mb-3">
                    <span className="text-sm text-gray-600">別名: </span>
                    {profile.aliases.map((alias, index) => (
                      <span key={alias.id} className="text-sm">
                        {alias.alias_name}
                        {index < profile.aliases.length - 1 ? ', ' : ''}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Physical Stats */}
            {formatPhysicalStats() && (
              <div className="mb-4">
                <span className="text-gray-600">身体情報: </span>
                <span className="font-medium">{formatPhysicalStats()}</span>
              </div>
            )}

            {/* IDs for debugging/admin */}
            {(profile.id || profile.dmmId) && (
              <div className="mb-4 text-sm">
                <div className="text-gray-500">
                  {profile.id && <span>DB ID: {profile.id}</span>}
                  {profile.id && profile.dmmId && <span className="mx-2">|</span>}
                  {profile.dmmId && <span>DMM ID: {profile.dmmId}</span>}
                </div>
              </div>
            )}

            {/* Birthdate and Age from DMM */}
            {(profile.dmm?.birthdate || profile.additionalInfo?.birthdate) && (
              <div className="mb-4">
                <span className="text-gray-600">生年月日: </span>
                <span className="font-medium">
                  {formatDate(profile.dmm?.birthdate || profile.additionalInfo?.birthdate)}
                  {profile.dmm?.birthdate && (
                    <span className="ml-2 text-gray-600">
                      ({Math.floor((new Date() - new Date(profile.dmm.birthdate)) / (365.25 * 24 * 60 * 60 * 1000))}歳)
                    </span>
                  )}
                </span>
              </div>
            )}

            {/* Career Info */}
            {(profile.additionalInfo?.birthplace || profile.additionalInfo?.agency || profile.dmm?.debutDate) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {profile.additionalInfo.birthplace && (
                  <div>
                    <span className="text-gray-600">出身地: </span>
                    <span className="font-medium">{profile.additionalInfo.birthplace}</span>
                  </div>
                )}
                {profile.additionalInfo.agency && (
                  <div>
                    <span className="text-gray-600">所属事務所: </span>
                    <span className="font-medium">{profile.additionalInfo.agency}</span>
                  </div>
                )}
                {profile.dmm?.debutDate && (
                  <div>
                    <span className="text-gray-600">デビュー日: </span>
                    <span className="font-medium">{formatDate(profile.dmm.debutDate)}</span>
                  </div>
                )}
                {/* Activity Period */}
                {(profile.dmm?.debutDate || profile.debutDate) && (
                  <div>
                    <span className="text-gray-600">活動期間: </span>
                    <span className="font-medium">
                      {(() => {
                        const debut = new Date(profile.dmm?.debutDate || profile.debutDate);
                        const retirement = profile.retirementDate ? new Date(profile.retirementDate) : new Date();
                        const years = Math.floor((retirement - debut) / (365.25 * 24 * 60 * 60 * 1000));
                        const months = Math.floor(((retirement - debut) % (365.25 * 24 * 60 * 60 * 1000)) / (30.44 * 24 * 60 * 60 * 1000));
                        
                        if (years === 0 && months === 0) return '1ヶ月未満';
                        if (years === 0) return `${months}ヶ月`;
                        if (months === 0) return `${years}年`;
                        return `${years}年${months}ヶ月`;
                      })()}
                      {profile.retirementDate && ' (引退)'}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Statistics */}
      {profile.stats && (
        <ActressStats stats={profile.stats} className="mb-6" />
      )}

      {/* Navigation Tabs */}
      <div className="flex space-x-1 mb-6">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                {tab.label}
                {tab.count !== null && ` (${tab.count})`}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'overview' && (
              <>
                <ActressSocialLinks 
                  social={profile.social} 
                  additionalInfo={profile.additionalInfo}
                  dmmUrl={profile.dmmUrl}
                />
                <ActressProfileDisplay profile={profile} />
              </>
            )}

            {activeTab === 'works' && (
              <ActressWorksList videos={profile.videos} />
            )}

            {activeTab === 'gallery' && (
              <ActressGallery images={profile.images} />
            )}

            {activeTab === 'profile' && (
              <ActressProfileDisplay profile={profile} />
            )}
          </div>
    </div>
  );
};

export default ActressDetailEnhanced;